import { ODOO_BASE_URL } from '../../odooConfig';

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Local requests go through setupProxy; deployed builds call the configured Odoo host.
const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;

/**
 * Marks Management API Client
 * Handles all API requests using JSON-RPC 2.0 protocol with Bearer Token authentication
 */
class MarksAPI {
  constructor(baseURL = BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Make a JSON-RPC 2.0 request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Request parameters
   * @param {string} token - Bearer token for authentication
   * @returns {Promise<object>} API response
   */
  async request(endpoint, params = {}, token = null) {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token is provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Use JSON-RPC 2.0 format (as per backend implementation)
    // Backend uses Odoo's type='json' which expects JSON-RPC 2.0 format
    const requestBody = {
      jsonrpc: '2.0',
      method: 'call',
      params: params
    };

    try {
      const fullUrl = `${this.baseURL}${endpoint}`;
      console.log(`Marks API Request to: ${fullUrl}`);
      console.log(`Marks API Request body (JSON-RPC 2.0):`, requestBody);
      console.log(`Marks API Headers:`, headers);
      console.log(`Marks API Has token:`, !!token);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      console.log(`Marks API Response status: ${response.status}`, response.statusText);

      // Check content type first
      const contentType = response.headers.get('content-type') || '';
      const isHTML = contentType.includes('text/html') || contentType.includes('text/plain');
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Marks API Error Response Status:', response.status, response.statusText);
        console.error('Marks API Error Response Headers:', Object.fromEntries(response.headers.entries()));
        console.error('Marks API Error Response Body:', errorText.substring(0, 1000));
        console.error('Marks API Full URL:', fullUrl);
        
        // Check if we got HTML instead of JSON (404 page)
        if (isHTML || errorText.trim().startsWith('<!DOCTYPE') || errorText.includes('<html')) {
          console.error('⚠️ Received HTML response instead of JSON. This usually means:');
          console.error('   1. The endpoint does not exist (404)');
          console.error('   2. The route is not registered in the backend');
          console.error('   3. The URL path is incorrect');
          console.error('   4. The backend server needs to be restarted');
          console.error('   Full URL attempted:', fullUrl);
          
          const errorMsg = `API endpoint not found (404). The endpoint ${endpoint} is not accessible on the server.\n\n` +
            `Full URL: ${fullUrl}\n\n` +
            `Possible issues:\n` +
            `1. The endpoint may not be registered in the backend routing\n` +
            `2. The backend server may need to be restarted\n` +
            `3. The URL path may be incorrect\n\n` +
            `Please contact your administrator to verify the Marks Management API endpoints:\n` +
            `- POST /api/marks/teacher-sheet/list\n` +
            `- POST /api/marks/teacher-sheet/{id}\n` +
            `- POST /api/marks/student-list\n\n` +
            `Implementation location: Shaag/openeducat_attendance/controllers/marks_api.py`;
          throw new Error(errorMsg);
        }
        
        // Try to parse as JSON to get error message
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            throw new Error(errorData.message);
          }
        } catch (e) {
          // Not JSON, use raw text
        }
        
        throw new Error(`API request failed: ${errorText.substring(0, 200) || response.statusText}`);
      }

      // Try to parse as JSON, but handle HTML responses
      let data;
      try {
        const responseText = await response.text();
        
        // Check if response is HTML
        if (isHTML || responseText.trim().startsWith('<!DOCTYPE') || responseText.includes('<html')) {
          console.error('Marks API received HTML instead of JSON:', responseText.substring(0, 500));
          const errorMsg = `API endpoint not found (404). The endpoint ${endpoint} is not implemented on the server.\n\n` +
            `Please contact your administrator to implement the Marks Management API endpoints:\n` +
            `- POST /api/marks/teacher-sheet/list\n` +
            `- POST /api/marks/teacher-sheet/{id}\n` +
            `- POST /api/marks/student-list`;
          throw new Error(errorMsg);
        }
        
        data = JSON.parse(responseText);
        console.log('Marks API Response data:', data);
      } catch (parseError) {
        if (parseError.message.includes('API endpoint not found')) {
          throw parseError; // Re-throw our custom error
        }
        console.error('Marks API JSON parse error:', parseError);
        throw new Error(`Failed to parse API response as JSON. The endpoint ${endpoint} may not be implemented correctly.`);
      }

      // Handle JSON-RPC 2.0 error response
      if (data.error) {
        console.error('Marks API JSON-RPC Error:', data.error);
        const errorMsg = data.error.message || data.error.data?.message || 'API request failed';
        throw new Error(errorMsg);
      }

      // Extract result from JSON-RPC 2.0 format
      // Odoo returns: { jsonrpc: "2.0", result: { success: true, ... } }
      const result = data.result || data;
      
      // Check for error in result (standardized error format)
      if (result && result.success === false) {
        const errorMsg = result.message || result.error || 'API request failed';
        console.error('❌ API returned success=false:', errorMsg);
        throw new Error(errorMsg);
      }

      // Return the result (it has success, data, etc.)
      console.log('Marks API Returning result:', result);
      return result;
    } catch (error) {
      console.error('Marks API request error:', error);
      throw error;
    }
  }

  /**
   * Ping endpoint to check API health
   * @param {string} token - Bearer token (optional)
   * @returns {Promise<object>} Ping response
   */
  async ping(token = null) {
    return await this.request('/api/marks/ping', {}, token);
  }

  /**
   * Get list of teacher sheet marks
   * @param {object} filters - Filter parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Teacher sheet marks list
   */
  async getTeacherSheetList(filters = {}, token) {
    try {
      const result = await this.request('/api/marks/teacher-sheet/list', filters, token);
      // API returns: { success: true, marks: [...], count: ... }
      return result;
    } catch (error) {
      console.error('Error in getTeacherSheetList:', error);
      // Return empty result structure if API fails
      return {
        success: false,
        marks: [],
        count: 0,
        error: error.message
      };
    }
  }

  /**
   * Get teacher sheet marks details
   * @param {number} sheetId - Sheet ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Teacher sheet marks details
   */
  async getTeacherSheetDetails(sheetId, token) {
    // API supports: POST /api/marks/teacher-sheet/{id} with empty body {}
    return await this.request(`/api/marks/teacher-sheet/${sheetId}`, {}, token);
  }

  /**
   * Update teacher sheet marks
   * @param {number} sheetId - Sheet ID
   * @param {array} entryLines - Array of mark updates
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Update response
   */
  async updateTeacherSheetMarks(sheetId, entryLines, token) {
    return await this.request('/api/marks/teacher-sheet/update', {
      sheet_id: sheetId,
      entry_lines: entryLines
    }, token);
  }

  /**
   * Search student marks
   * @param {string} studentNo - Student number
   * @param {string} semester - Semester number
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Student marks
   */
  async searchStudentMarks(studentNo, semester, token) {
    return await this.request('/api/marks/student/search', {
      student_no: studentNo,
      semester: semester
    }, token);
  }

  /** Resolve student information while the ID is entered. */
  async lookupStudent(studentNo, token) {
    return await this.request('/api/marks/student/lookup', { student_no: studentNo }, token);
  }

  /** Get the official transcript through the selected semester. */
  async getStudentTranscript(studentNo, semester, token) {
    return await this.request('/api/marks/student/transcript', {
      student_no: studentNo,
      semester: semester
    }, token);
  }
  /**
   * Get filtered student list
   * @param {object} filters - Filter parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Student list
   */
  async getStudentList(filters = {}, token) {
    return await this.request('/api/marks/student-list', filters, token);
  }

  /**
   * Get list of faculties from teacher sheets
   * Uses teacher sheet list to extract unique faculties
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Faculties list
   */
  async getFaculties(token) {
    try {
      // Get all teacher sheets to extract unique faculties
      const result = await this.getTeacherSheetList({ limit: 1000 }, token);
      // API returns: { success: true, marks: [...], count: ... }
      if (result.success && result.marks && result.marks.length > 0) {
        const facultyMap = new Map();
        result.marks.forEach(sheet => {
          if (sheet.faculty_id) {
            if (!facultyMap.has(sheet.faculty_id)) {
              facultyMap.set(sheet.faculty_id, {
                id: sheet.faculty_id,
                name: sheet.faculty_name || `Faculty ${sheet.faculty_id}`
              });
            }
          }
        });
        return {
          success: true,
          faculties: Array.from(facultyMap.values())
        };
      }
      return { success: false, faculties: [], message: 'No teacher sheets found' };
    } catch (error) {
      console.error('Error in getFaculties:', error);
      return { success: false, faculties: [], error: error.message };
    }
  }

  /**
   * Get list of departments for a faculty from teacher sheets
   * @param {number} facultyId - Faculty ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Departments list
   */
  async getDepartments(facultyId, token) {
    // Get teacher sheets for this faculty to extract unique departments
    const result = await this.getTeacherSheetList({ 
      faculty_id: facultyId, 
      limit: 1000 
    }, token);
    // API returns: { success: true, marks: [...], count: ... }
    if (result.success && result.marks) {
      const deptMap = new Map();
      result.marks.forEach(sheet => {
        if (sheet.department_id) {
          if (!deptMap.has(sheet.department_id)) {
            deptMap.set(sheet.department_id, {
              id: sheet.department_id,
              name: sheet.department_name || `Department ${sheet.department_id}`
            });
          }
        }
      });
      return {
        success: true,
        departments: Array.from(deptMap.values())
      };
    }
    return { success: false, departments: [] };
  }

  /**
   * Get list of batches for a faculty and department from teacher sheets
   * @param {number} facultyId - Faculty ID
   * @param {number} departmentId - Department ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Batches list
   */
  async getBatchesForFacultyDept(facultyId, departmentId, token) {
    // Get teacher sheets for this faculty and department to extract unique batches
    const result = await this.getTeacherSheetList({ 
      faculty_id: facultyId,
      department_id: departmentId,
      limit: 1000 
    }, token);
    // API returns: { success: true, marks: [...], count: ... }
    if (result.success && result.marks) {
      const batchMap = new Map();
      result.marks.forEach(sheet => {
        if (sheet.batch_id) {
          if (!batchMap.has(sheet.batch_id)) {
            batchMap.set(sheet.batch_id, {
              id: sheet.batch_id,
              name: sheet.batch_name || `Batch ${sheet.batch_id}`
            });
          }
        }
      });
      return {
        success: true,
        batches: Array.from(batchMap.values())
      };
    }
    return { success: false, batches: [] };
  }
}

// Export a singleton instance
const marksAPI = new MarksAPI();
export default marksAPI;
